import { Card, CardContent, toast } from '@estac1/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../lib/api.js';
import { FlagRow, NumberField } from './billing-form-fields.js';
import type { BillingFlags } from './types.js';

/**
 * Data-retention sweep controls (#217). Master toggle plus per-table windows in
 * days (0 = never prune). The audit-log window defaults to 0 because the trail
 * is compliance-sensitive; enabling it should be paired with an export.
 */
export const RetentionCard = () => {
  const qc = useQueryClient();
  const flags = useQuery({
    queryKey: ['admin-settings-flags'],
    queryFn: () => api.request<BillingFlags>('/v1/admin/settings/flags'),
  });

  const update = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      api.request<BillingFlags>('/v1/admin/settings/flags', { method: 'PUT', json: patch }),
    onSuccess: () => {
      toast.success('Retenção de dados atualizada.');
      qc.invalidateQueries({ queryKey: ['admin-settings-flags'] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Falha ao salvar.'),
  });

  const f = flags.data;

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div>
          <h2 className="text-sm font-semibold">Retenção de dados</h2>
          <p className="text-xs text-muted-fg">
            Limpeza diária de tabelas que só crescem (SMS, links, notificações). O D1 tem teto de 10
            GB por banco. Janela em dias; 0 desativa a limpeza daquela tabela.
          </p>
        </div>
        {flags.isLoading || !f ? (
          <p className="text-sm text-muted-fg">Carregando...</p>
        ) : (
          <div className="space-y-3">
            <FlagRow
              label="Limpeza automática"
              hint="Liga a rotina diária de retenção. Com ela desligada, nada é apagado."
              enabled={f.retentionSweepEnabled}
              pending={update.isPending}
              onToggle={(v) => update.mutate({ retentionSweepEnabled: v })}
            />
            <div
              className={
                f.retentionSweepEnabled
                  ? 'grid gap-3 sm:grid-cols-2'
                  : 'grid gap-3 opacity-50 sm:grid-cols-2'
              }
            >
              <NumberField
                label="Log de SMS"
                value={f.retention.smsLogDays}
                disabled={!f.retentionSweepEnabled || update.isPending}
                onSave={(v) => update.mutate({ retentionSmsLogDays: v })}
              />
              <NumberField
                label="Notificações"
                value={f.retention.notificationsDays}
                disabled={!f.retentionSweepEnabled || update.isPending}
                onSave={(v) => update.mutate({ retentionNotificationsDays: v })}
              />
              <NumberField
                label="Links de pagamento"
                hint="Só apaga links já pagos ou vencidos."
                value={f.retention.paymentLinksDays}
                disabled={!f.retentionSweepEnabled || update.isPending}
                onSave={(v) => update.mutate({ retentionPaymentLinksDays: v })}
              />
              <NumberField
                label="Carência de magic links"
                hint="Dias após expirar antes de apagar."
                value={f.retention.magicLinksGraceDays}
                disabled={!f.retentionSweepEnabled || update.isPending}
                onSave={(v) => update.mutate({ retentionMagicLinksGraceDays: v })}
              />
              <NumberField
                label="Auditoria"
                hint="0 = nunca apagar. Ligue só com exportação combinada."
                value={f.retention.auditLogDays}
                disabled={!f.retentionSweepEnabled || update.isPending}
                onSave={(v) => update.mutate({ retentionAuditLogDays: v })}
              />
              <NumberField
                label="Fotos de avaria"
                hint="Dias após o fechamento. A política publicada promete 90 — mude as duas juntas."
                value={f.retention.damagePhotosDays}
                disabled={!f.retentionSweepEnabled || update.isPending}
                onSave={(v) => update.mutate({ retentionDamagePhotosDays: v })}
              />
              <NumberField
                label="Link público do ticket"
                hint="Dias após o fechamento em que o link do SMS ainda abre. Não depende da limpeza."
                value={f.retention.publicTicketGraceDays}
                disabled={update.isPending}
                onSave={(v) => update.mutate({ retentionPublicTicketGraceDays: v })}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
